import {
  STUDIO_PUBLICATION_DESCRIPTOR_SCHEMA_VERSION,
  assertStudioPublicationDescriptor,
  createStudioPublicationDescriptor,
} from "../StudioPublicationDescriptor";

const PUBLISHED_STATUS = "published";

export class ActivePublicationResolverError extends Error {
  constructor(
    message,
    {
      code = "ACTIVE_PUBLICATION_RESOLVER_ERROR",
      operation = null,
      publicationId = null,
      documentId = null,
      details = {},
      cause = null,
    } = {}
  ) {
    super(message);

    this.name = "ActivePublicationResolverError";
    this.code = code;
    this.operation = operation;
    this.publicationId = publicationId;
    this.documentId = documentId;
    this.details = Object.freeze({
      ...details,
    });

    if (cause !== null) {
      this.cause = cause;
    }
  }
}

/**
 * Creates an isolated, stateless Active Publication Resolver.
 *
 * Dependencies:
 *
 * publicationEngine:
 *   - getCurrentPublication(documentId, { context })
 *   - getPublication(publicationId, { context })
 *
 * getVersion:
 *   - resolves an immutable LayoutVersion by versionId
 *
 * resolveStudioReference:
 *   - resolves Runtime scope to:
 *       studioId
 *       documentId
 *       domainId
 *       runtimeVersion
 *
 * createDescriptorId:
 *   - produces the public StudioPublicationDescriptor id
 *
 * The resolver does not:
 *   - publish
 *   - mutate publications
 *   - access Studio drafts
 *   - access persistence directly
 *   - select a StudioPublicationAdapter
 *   - produce Domain Runtime Configuration
 */
export function createActivePublicationResolver({
  publicationEngine,
  getVersion,
  resolveStudioReference,
  createDescriptorId = defaultDescriptorIdFactory,
} = {}) {
  assertPublicationEngine(publicationEngine);

  if (typeof getVersion !== "function") {
    throw new ActivePublicationResolverError(
      "getVersion must be a function.",
      {
        code: "INVALID_VERSION_RESOLVER",
      }
    );
  }

  if (typeof resolveStudioReference !== "function") {
    throw new ActivePublicationResolverError(
      "resolveStudioReference must be a function.",
      {
        code: "INVALID_STUDIO_REFERENCE_RESOLVER",
      }
    );
  }

  if (typeof createDescriptorId !== "function") {
    throw new ActivePublicationResolverError(
      "createDescriptorId must be a function.",
      {
        code: "INVALID_DESCRIPTOR_ID_FACTORY",
      }
    );
  }

  async function resolveActivePublication(scope) {
    const operation = "resolveActivePublication";
    const normalizedScope =
      normalizePublicationScope(scope);

    try {
      const reference =
        await resolveAndValidateStudioReference(
          normalizedScope,
          resolveStudioReference,
          operation
        );

      const context =
        createResolverContext(
          normalizedScope,
          reference
        );

      const publication =
        await publicationEngine.getCurrentPublication(
          reference.documentId,
          {
            context,
          }
        );

      if (publication === null) {
        return null;
      }

      return await buildDescriptor({
        operation,
        publication,
        reference,
        scope: normalizedScope,
        context,
        getVersion,
        createDescriptorId,
      });
    } catch (error) {
      throw wrapResolverError(
        error,
        "Unable to resolve active Studio publication.",
        {
          code: "RESOLVE_ACTIVE_PUBLICATION_FAILED",
          operation,
          details: {
            scope: normalizedScope,
          },
        }
      );
    }
  }

  async function resolvePublicationById(
    publicationId,
    scope
  ) {
    const operation = "resolvePublicationById";

    const normalizedPublicationId =
      normalizeRequiredString(
        publicationId,
        "publicationId",
        {
          code: "INVALID_PUBLICATION_ID",
        }
      );

    const normalizedScope =
      normalizePublicationScope(scope);

    try {
      const reference =
        await resolveAndValidateStudioReference(
          normalizedScope,
          resolveStudioReference,
          operation
        );

      const context =
        createResolverContext(
          normalizedScope,
          reference
        );

      const publication =
        await publicationEngine.getPublication(
          normalizedPublicationId,
          {
            context,
          }
        );

      if (
        publication === null ||
        typeof publication !== "object"
      ) {
        throw new ActivePublicationResolverError(
          "PublicationEngine returned an invalid publication.",
          {
            code: "INVALID_PUBLICATION_RESULT",
            operation,
            publicationId:
              normalizedPublicationId,
          }
        );
      }

      if (
        publication.status !==
        PUBLISHED_STATUS
      ) {
        throw new ActivePublicationResolverError(
          "Studio publication is not active.",
          {
            code: "PUBLICATION_NOT_ACTIVE",
            operation,
            publicationId:
              normalizedPublicationId,
            documentId:
              publication.documentId ?? null,
            details: {
              status:
                publication.status ?? null,
            },
          }
        );
      }

      return await buildDescriptor({
        operation,
        publication,
        reference,
        scope: normalizedScope,
        context,
        getVersion,
        createDescriptorId,
      });
    } catch (error) {
      throw wrapResolverError(
        error,
        "Unable to resolve Studio publication by id.",
        {
          code: "RESOLVE_PUBLICATION_BY_ID_FAILED",
          operation,
          publicationId:
            normalizedPublicationId,
          details: {
            scope: normalizedScope,
          },
        }
      );
    }
  }

  function assertPublicationScope(
    descriptor,
    scope
  ) {
    return assertPublicationScopeContract(
      descriptor,
      scope
    );
  }

  function assertPublicationCompatibility(
    descriptor,
    {
      expectedSchemaVersion =
        STUDIO_PUBLICATION_DESCRIPTOR_SCHEMA_VERSION,
      expectedRuntimeVersion = null,
    } = {}
  ) {
    return assertPublicationCompatibilityContract(
      descriptor,
      {
        expectedSchemaVersion,
        expectedRuntimeVersion,
      }
    );
  }

  return Object.freeze({
    resolveActivePublication,
    resolvePublicationById,
    assertPublicationScope,
    assertPublicationCompatibility,
  });
}

/**
 * Public scope assertion.
 *
 * The descriptor is already immutable and validated by the descriptor
 * contract. This helper only compares Runtime scope identities.
 */
export function assertPublicationScope(
  descriptor,
  scope
) {
  return assertPublicationScopeContract(
    descriptor,
    scope
  );
}

/**
 * Public compatibility assertion.
 *
 * Domain adapter compatibility remains owned by
 * StudioPublicationAdapter and the future Runtime Bridge.
 */
export function assertPublicationCompatibility(
  descriptor,
  options = {}
) {
  return assertPublicationCompatibilityContract(
    descriptor,
    options
  );
}

function assertPublicationScopeContract(
  descriptor,
  scope
) {
  assertStudioPublicationDescriptor(
    descriptor
  );

  const normalizedScope =
    normalizePublicationScope(scope);

  const checks = [
    [
      "companyUuid",
      descriptor.companyUuid,
      normalizedScope.companyUuid,
    ],
    [
      "activityUuid",
      descriptor.activityUuid,
      normalizedScope.activityUuid,
    ],
    [
      "domainId",
      descriptor.domainId,
      normalizedScope.domainId,
    ],
  ];

  for (
    const [
      fieldName,
      actualValue,
      expectedValue,
    ]
    of checks
  ) {
    if (actualValue !== expectedValue) {
      throw new ActivePublicationResolverError(
        `Studio publication ${fieldName} does not match the requested scope.`,
        {
          code: "PUBLICATION_SCOPE_MISMATCH",
          publicationId:
            descriptor.publicationId,
          documentId:
            descriptor.documentId,
          details: {
            field: fieldName,
            expected: expectedValue,
            actual: actualValue,
          },
        }
      );
    }
  }

  return descriptor;
}

function assertPublicationCompatibilityContract(
  descriptor,
  {
    expectedSchemaVersion =
      STUDIO_PUBLICATION_DESCRIPTOR_SCHEMA_VERSION,
    expectedRuntimeVersion = null,
  } = {}
) {
  assertStudioPublicationDescriptor(
    descriptor
  );

  const normalizedSchemaVersion =
    normalizePositiveInteger(
      expectedSchemaVersion,
      "expectedSchemaVersion",
      {
        code:
          "INVALID_EXPECTED_SCHEMA_VERSION",
      }
    );

  if (
    descriptor.schemaVersion !==
    normalizedSchemaVersion
  ) {
    throw new ActivePublicationResolverError(
      "Studio publication schemaVersion is incompatible with the resolver.",
      {
        code:
          "INCOMPATIBLE_SCHEMA_VERSION",
        publicationId:
          descriptor.publicationId,
        documentId:
          descriptor.documentId,
        details: {
          expectedSchemaVersion:
            normalizedSchemaVersion,
          actualSchemaVersion:
            descriptor.schemaVersion,
        },
      }
    );
  }

  if (
    expectedRuntimeVersion !== null &&
    expectedRuntimeVersion !== undefined
  ) {
    const normalizedRuntimeVersion =
      normalizePositiveInteger(
        expectedRuntimeVersion,
        "expectedRuntimeVersion",
        {
          code:
            "INVALID_EXPECTED_RUNTIME_VERSION",
        }
      );

    if (
      descriptor.runtimeVersion !==
      normalizedRuntimeVersion
    ) {
      throw new ActivePublicationResolverError(
        "Studio publication runtimeVersion is incompatible with the resolver.",
        {
          code:
            "INCOMPATIBLE_RUNTIME_VERSION",
          publicationId:
            descriptor.publicationId,
          documentId:
            descriptor.documentId,
          details: {
            expectedRuntimeVersion:
              normalizedRuntimeVersion,
            actualRuntimeVersion:
              descriptor.runtimeVersion,
          },
        }
      );
    }
  }

  return descriptor;
}

async function buildDescriptor({
  operation,
  publication,
  reference,
  scope,
  context,
  getVersion,
  createDescriptorId,
}) {
  validatePublicationEnvelope(
    publication,
    {
      operation,
    }
  );

  if (
    publication.documentId !==
    reference.documentId
  ) {
    throw new ActivePublicationResolverError(
      "Publication does not belong to the resolved Studio document.",
      {
        code: "PUBLICATION_SCOPE_MISMATCH",
        operation,
        publicationId:
          publication.publicationId,
        documentId:
          publication.documentId,
        details: {
          expectedDocumentId:
            reference.documentId,
          actualDocumentId:
            publication.documentId,
        },
      }
    );
  }

  const version =
    await getVersion(
      publication.versionId,
      {
        context,
      }
    );

  if (
    version === null ||
    version === undefined
  ) {
    throw new ActivePublicationResolverError(
      `Layout version not found: ${publication.versionId}.`,
      {
        code: "VERSION_NOT_FOUND",
        operation,
        publicationId:
          publication.publicationId,
        documentId:
          publication.documentId,
        details: {
          versionId:
            publication.versionId,
        },
      }
    );
  }

  validateLayoutVersion(
    version,
    {
      publication,
      operation,
    }
  );

  const descriptorId =
    normalizeRequiredString(
      await createDescriptorId({
        publication,
        version,
        scope,
        reference,
      }),
      "descriptorId",
      {
        code: "INVALID_DESCRIPTOR_ID",
      }
    );

  const descriptor =
    createStudioPublicationDescriptor({
      id: descriptorId,
      studioId: reference.studioId,
      domainId: reference.domainId,
      companyUuid: scope.companyUuid,
      activityUuid: scope.activityUuid,
      documentId:
        publication.documentId,
      versionId:
        publication.versionId,
      publicationId:
        publication.publicationId,
      publishedAt:
        publication.publishedAt,
      schemaVersion:
        STUDIO_PUBLICATION_DESCRIPTOR_SCHEMA_VERSION,
      runtimeVersion:
        reference.runtimeVersion,
      payload:
        version.serializedDocument,
      metadata:
        publication.metadata ?? {},
    });

  assertPublicationScopeContract(
    descriptor,
    scope
  );

  assertPublicationCompatibilityContract(
    descriptor,
    {
      expectedSchemaVersion:
        STUDIO_PUBLICATION_DESCRIPTOR_SCHEMA_VERSION,
      expectedRuntimeVersion:
        reference.runtimeVersion,
    }
  );

  return descriptor;
}

async function resolveAndValidateStudioReference(
  scope,
  resolveStudioReference,
  operation
) {
  const reference =
    await resolveStudioReference(
      scope
    );

  if (
    reference === null ||
    typeof reference !== "object" ||
    Array.isArray(reference)
  ) {
    throw new ActivePublicationResolverError(
      "resolveStudioReference must return an object.",
      {
        code: "INVALID_STUDIO_REFERENCE",
        operation,
      }
    );
  }

  const normalizedReference = {
    studioId:
      normalizeRequiredString(
        reference.studioId,
        "studioId",
        {
          code:
            "INVALID_STUDIO_REFERENCE",
        }
      ),

    documentId:
      normalizeRequiredString(
        reference.documentId,
        "documentId",
        {
          code:
            "INVALID_STUDIO_REFERENCE",
        }
      ),

    domainId:
      normalizeRequiredString(
        reference.domainId,
        "domainId",
        {
          code:
            "INVALID_STUDIO_REFERENCE",
        }
      ),

    runtimeVersion:
      normalizePositiveInteger(
        reference.runtimeVersion,
        "runtimeVersion",
        {
          code:
            "INVALID_STUDIO_REFERENCE",
        }
      ),
  };

  if (
    normalizedReference.domainId !==
    scope.domainId
  ) {
    throw new ActivePublicationResolverError(
      "Resolved Studio reference belongs to another domain.",
      {
        code: "PUBLICATION_SCOPE_MISMATCH",
        operation,
        documentId:
          normalizedReference.documentId,
        details: {
          field: "domainId",
          expected: scope.domainId,
          actual:
            normalizedReference.domainId,
        },
      }
    );
  }

  return Object.freeze(
    normalizedReference
  );
}

function validatePublicationEnvelope(
  publication,
  {
    operation,
  }
) {
  if (
    publication === null ||
    typeof publication !== "object" ||
    Array.isArray(publication)
  ) {
    throw new ActivePublicationResolverError(
      "PublicationEngine returned an invalid publication.",
      {
        code:
          "INVALID_PUBLICATION_RESULT",
        operation,
      }
    );
  }

  const publicationId =
    normalizeRequiredString(
      publication.publicationId,
      "publicationId",
      {
        code:
          "INVALID_PUBLICATION_RESULT",
      }
    );

  const documentId =
    normalizeRequiredString(
      publication.documentId,
      "documentId",
      {
        code:
          "INVALID_PUBLICATION_RESULT",
      }
    );

  normalizeRequiredString(
    publication.versionId,
    "versionId",
    {
      code:
        "INVALID_PUBLICATION_RESULT",
    }
  );

  normalizeRequiredString(
    publication.publishedAt,
    "publishedAt",
    {
      code:
        "INVALID_PUBLICATION_RESULT",
    }
  );

  if (
    publication.status !==
    PUBLISHED_STATUS
  ) {
    throw new ActivePublicationResolverError(
      "Studio publication is not active.",
      {
        code: "PUBLICATION_NOT_ACTIVE",
        operation,
        publicationId,
        documentId,
        details: {
          status:
            publication.status ?? null,
        },
      }
    );
  }
}

function validateLayoutVersion(
  version,
  {
    publication,
    operation,
  }
) {
  if (
    version === null ||
    typeof version !== "object" ||
    Array.isArray(version)
  ) {
    throw new ActivePublicationResolverError(
      "Layout version resolver returned an invalid value.",
      {
        code: "INVALID_VERSION_RESULT",
        operation,
        publicationId:
          publication.publicationId,
        documentId:
          publication.documentId,
      }
    );
  }

  const versionId =
    normalizeRequiredString(
      version.versionId,
      "versionId",
      {
        code:
          "INVALID_VERSION_RESULT",
      }
    );

  const documentId =
    normalizeRequiredString(
      version.documentId,
      "documentId",
      {
        code:
          "INVALID_VERSION_RESULT",
      }
    );

  if (
    versionId !== publication.versionId
  ) {
    throw new ActivePublicationResolverError(
      "Resolved LayoutVersion does not match the publication versionId.",
      {
        code: "VERSION_MISMATCH",
        operation,
        publicationId:
          publication.publicationId,
        documentId:
          publication.documentId,
        details: {
          expectedVersionId:
            publication.versionId,
          actualVersionId:
            versionId,
        },
      }
    );
  }

  if (
    documentId !== publication.documentId
  ) {
    throw new ActivePublicationResolverError(
      "Resolved LayoutVersion belongs to another Studio document.",
      {
        code: "VERSION_DOCUMENT_MISMATCH",
        operation,
        publicationId:
          publication.publicationId,
        documentId:
          publication.documentId,
        details: {
          expectedDocumentId:
            publication.documentId,
          actualDocumentId:
            documentId,
        },
      }
    );
  }

  if (
    version.serializedDocument === null ||
    typeof version.serializedDocument !==
      "object" ||
    Array.isArray(
      version.serializedDocument
    )
  ) {
    throw new ActivePublicationResolverError(
      "Resolved LayoutVersion does not contain a valid serializedDocument.",
      {
        code: "INVALID_VERSION_PAYLOAD",
        operation,
        publicationId:
          publication.publicationId,
        documentId:
          publication.documentId,
      }
    );
  }
}

function normalizePublicationScope(scope) {
  if (
    scope === null ||
    typeof scope !== "object" ||
    Array.isArray(scope)
  ) {
    throw new ActivePublicationResolverError(
      "Publication scope must be an object.",
      {
        code: "INVALID_SCOPE",
      }
    );
  }

  return Object.freeze({
    companyUuid:
      normalizeRequiredString(
        scope.companyUuid,
        "companyUuid",
        {
          code: "INVALID_SCOPE",
        }
      ),

    activityUuid:
      normalizeRequiredString(
        scope.activityUuid,
        "activityUuid",
        {
          code: "INVALID_SCOPE",
        }
      ),

    domainId:
      normalizeRequiredString(
        scope.domainId,
        "domainId",
        {
          code: "INVALID_SCOPE",
        }
      ),
  });
}

function createResolverContext(
  scope,
  reference
) {
  return Object.freeze({
    companyUuid: scope.companyUuid,
    activityUuid: scope.activityUuid,
    domainId: scope.domainId,
    studioId: reference.studioId,
    documentId: reference.documentId,
  });
}

function assertPublicationEngine(
  publicationEngine
) {
  if (
    publicationEngine === null ||
    typeof publicationEngine !== "object"
  ) {
    throw new ActivePublicationResolverError(
      "publicationEngine must be an object.",
      {
        code:
          "INVALID_PUBLICATION_ENGINE",
      }
    );
  }

  for (const methodName of [
    "getCurrentPublication",
    "getPublication",
  ]) {
    if (
      typeof publicationEngine[
        methodName
      ] !== "function"
    ) {
      throw new ActivePublicationResolverError(
        `publicationEngine requires ${methodName}().`,
        {
          code:
            "INVALID_PUBLICATION_ENGINE",
          details: {
            methodName,
          },
        }
      );
    }
  }
}

function normalizeRequiredString(
  value,
  fieldName,
  {
    code = "INVALID_VALUE",
  } = {}
) {
  if (
    typeof value !== "string" ||
    value.trim() === ""
  ) {
    throw new ActivePublicationResolverError(
      `${fieldName} must be a non-empty string.`,
      {
        code,
        details: {
          fieldName,
        },
      }
    );
  }

  return value.trim();
}

function normalizePositiveInteger(
  value,
  fieldName,
  {
    code = "INVALID_VALUE",
  } = {}
) {
  if (
    !Number.isInteger(value) ||
    value <= 0
  ) {
    throw new ActivePublicationResolverError(
      `${fieldName} must be a positive integer.`,
      {
        code,
        details: {
          fieldName,
        },
      }
    );
  }

  return value;
}

function defaultDescriptorIdFactory({
  publication,
}) {
  return publication.publicationId;
}

function wrapResolverError(
  error,
  message,
  {
    code,
    operation,
    publicationId = null,
    documentId = null,
    details = {},
  }
) {
  if (
    error instanceof
    ActivePublicationResolverError
  ) {
    return error;
  }

  return new ActivePublicationResolverError(
    message,
    {
      code,
      operation,
      publicationId,
      documentId,
      details,
      cause: error,
    }
  );
}
